import { type ReactNode, useEffect, useId, useRef } from "react";
import type * as React from "react";
import { Icon } from "../icon/Icon";

/**
 * Centered modal dialog. Keeps the Wanted API and look (components/overlays/Dialog.jsx: radius-xl,
 * shadow-overlay, 24px padding, actions bottom-right) but renders a native <dialog> with
 * showModal(), so the browser handles aria-modal, ESC, focus trap, inert background and focus
 * return to the trigger.
 */
export interface DialogProps {
  /** @default true */
  open?: boolean;
  title?: string;
  /** Optional line under the title (linked with aria-describedby). */
  description?: ReactNode;
  children?: ReactNode;
  /** Primary action node (e.g. a solid Button), placed on the right. */
  primaryAction?: ReactNode;
  /** Secondary action node, placed left of the primary. */
  secondaryAction?: ReactNode;
  /** Called on ESC, backdrop click and the close button. */
  onClose?: () => void;
  /** Dialog width in px. @default 360 */
  width?: number;
  /** Element to focus first instead of the first focusable one. */
  initialFocusRef?: React.RefObject<HTMLElement | null>;
  style?: React.CSSProperties;
}

export function Dialog({
  open = true,
  title,
  description,
  children,
  primaryAction,
  secondaryAction,
  onClose,
  width = 360,
  initialFocusRef,
  style,
}: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const descId = useId();

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open) {
      if (!dialog.open) {
        if (typeof dialog.showModal === "function") dialog.showModal();
        else dialog.setAttribute("open", "");
      }
      initialFocusRef?.current?.focus();
    } else if (dialog.open) {
      dialog.close();
    }
  }, [open, initialFocusRef]);

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: backdrop click is a mouse convenience; ESC and the close button cover keyboard users.
    <dialog
      ref={ref}
      aria-labelledby={title ? titleId : undefined}
      aria-describedby={description ? descId : undefined}
      onClose={() => onClose?.()}
      onClick={(e) => {
        if (e.target === ref.current) ref.current?.close();
      }}
      style={{
        width,
        maxWidth: "calc(100vw - 32px)",
        maxHeight: "calc(100dvh - 32px)",
        margin: "auto",
        padding: 0,
        border: "none",
        background: "var(--background-elevated-normal)",
        color: "var(--label-normal)",
        borderRadius: "var(--radius-xl)",
        boxShadow: "var(--shadow-overlay)",
        fontFamily: "var(--font-sans)",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          padding: 24,
          maxHeight: "calc(100dvh - 32px)",
          overflowY: "auto",
          boxSizing: "border-box",
          ...style,
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 8 }}>
          {title && (
            <h2
              id={titleId}
              style={{
                flex: 1,
                margin: 0,
                fontSize: "var(--font-size-heading-2)",
                lineHeight: "var(--line-height-heading-2)",
                fontWeight: 700,
                color: "var(--label-normal)",
              }}
            >
              {title}
            </h2>
          )}
          <button
            type="button"
            onClick={() => ref.current?.close()}
            aria-label="닫기"
            className="wds-pressable"
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              flex: "none",
              width: 40,
              height: 40,
              marginTop: -8,
              marginRight: -8,
              marginLeft: "auto",
              border: "none",
              borderRadius: "var(--radius-sm)",
              background: "transparent",
              color: "var(--label-neutral)",
              cursor: "pointer",
            }}
          >
            <Icon name="close" size={20} />
          </button>
        </div>
        {description && (
          <p
            id={descId}
            style={{
              margin: "0 0 16px",
              fontSize: "var(--font-size-body-2)",
              lineHeight: "var(--line-height-body-2-reading)",
              color: "var(--label-neutral)",
            }}
          >
            {description}
          </p>
        )}
        {children && (
          <div
            style={{
              fontSize: "var(--font-size-body-2)",
              lineHeight: "var(--line-height-body-2-reading)",
              color: "var(--label-neutral)",
            }}
          >
            {children}
          </div>
        )}
        {(primaryAction || secondaryAction) && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 24 }}>
            {secondaryAction}
            {primaryAction}
          </div>
        )}
      </div>
    </dialog>
  );
}

export default Dialog;
